import { h as __h } from "preact";
import { Component, h } from "preact";
import { createEnterpriseAPIKeyResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, response } from "~/lib/api.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import { object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import Select from "~/component/Select.js";
/** @import { FreshKey } from "~/admin/component/FreshKeyCallout.js"; */

import adminStyle from "~/admin/lib/style.js";
import input from "~/style/input.js";
const OPTIONS = [{
  days: 0,
  label: "Never"
}, {
  days: 1,
  label: "In 1 day"
}, {
  days: 7,
  label: "In 7 days"
}, {
  days: 30,
  label: "In 30 days"
}, {
  days: 90,
  label: "In 90 days"
}];

/**
 * @typedef {object} CreateApiKeyBody
 * @property {string} name
 * @property {string} description
 * @property {string} [expires_at]
 */

/** @typedef {(json: CreateApiKeyBody) => Promise<FreshKey>} SubmitCallback */

/** @param {{ enterpriseId: string }} props */
export const addApiKey = async ({
  enterpriseId
}) => {
  /** @type {SubmitCallback} */
  const onSubmit = async json => {
    const result = await authedApiRequest("POST", `/v2/enterprises/${enterpriseId}/api-keys`, {
      json,
      schema: response(createEnterpriseAPIKeyResponse)
    });
    Query.invalidate([`/v2/enterprises/${enterpriseId}/api-keys`]);
    return {
      name: json.name,
      secret: result.data.key
    };
  };
  return await modal({
    title: "New API key",
    content: (/** @type {(result?: FreshKey) => void} */resolve) => h(ModalChildren, {
      resolve,
      onSubmit
    })
  });
};

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {(result?: FreshKey) => void} resolve
 * @property {SubmitCallback} onSubmit
 */
class ModalChildren extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const {
      name,
      description,
      expires_in
    } = parse(object({
      name: string,
      description: string,
      expires_in: string
    }), Object.fromEntries(form.entries()));
    const days = Number(expires_in);
    this.props.resolve(await this.props.onSubmit({
      name,
      description,
      ...(days && {
        expires_at: new Date(Date.now() + days * (24 * 60 * 60 * 1000)).toISOString()
      })
    }));
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);
    return __h("form", {
      "class": adminStyle.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h(Field, {
      "for": "name",
      label: "Key name",
      validity: {
        valueMissing: "Please enter a name for the key."
      },
      error: error?.fields.name?.message
    }, __h("input", {
      "class": input.input,
      id: "name",
      name: "name",
      required: true,
      autofocus: true
    })), __h(Field, {
      "for": "description",
      label: "Key description (optional)",
      error: error?.fields.description?.message
    }, __h("input", {
      "class": input.input,
      id: "description",
      name: "description"
    })), __h(Field, {
      "for": "expires_in",
      label: "Expires",
      error: error?.fields.expires_at?.message
    }, __h(Select, {
      id: "expires_in",
      name: "expires_in"
    }, OPTIONS.map(o => __h("option", {
      key: o.days,
      value: o.days
    }, o.label)))), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Generate key"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}